import {
	ButtonDropdownField,
	DropdownList,
	FlexColumn,
	FlexRow,
	Label,
	Modal,
	Separator,
	Text,
} from '@forecast-it/design-system';
import React, {useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import styled from 'styled-components';
import {MODAL_TYPE, showModal} from '../../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../../shared/components/modals/types';
import {EntityType} from '../../Constants';
import type {DeleteCustomFieldDropdownOptionConfirmationModal_viewer$data as DeleteCustomFieldDropdownOptionConfirmationModal_viewer} from './__generated__/DeleteCustomFieldDropdownOptionConfirmationModal_viewer.graphql';
import {UnsavedDropdownOption} from './hooks/useDropdownOptions';
import {DropdownOption} from './types';

type DeleteCustomFieldDropdownOptionConfirmationModalProps = ModalProps<
	{
		viewer: DeleteCustomFieldDropdownOptionConfirmationModal_viewer;
	} & DeleteCustomFieldDropdownOptionConfirmationModalInput
>;

type DeleteCustomFieldDropdownOptionConfirmationModalInput = {
	// Called with the replacement option's id, or null to take the option off the records that use it.
	onConfirm: (replacementOptionId: string | null) => void;
	selectedOption: UnsavedDropdownOption;
	customFieldId: string;
	// What the field applies to. The dialog names the records that use the option after it.
	entityType: EntityType;
};

// The choice that takes the option off the records that use it and puts nothing in its place.
const REMOVE = 'REMOVE';
type ReplacementChoice = DropdownOption | typeof REMOVE;

const StyledDiv = styled.div`
	margin: 8px 0;
	width: 288px;
`;

export const showDeleteCustomFieldDropdownOptionConfirmationModal = (
	props: DeleteCustomFieldDropdownOptionConfirmationModalInput
): void => {
	showModal({
		type: MODAL_TYPE.DELETE_CUSTOM_FIELD_DROPDOWN_OPTION_CONFIRMATION_MODAL,
		...props,
	});
};

const DeleteCustomFieldDropdownOptionConfirmationModalInternal = ({
	closeModal,
	onConfirm,
	selectedOption: unsavedOption,
	entityType,
	viewer,
}: ModalProps<DeleteCustomFieldDropdownOptionConfirmationModalProps>): JSX.Element => {
	const {formatMessage} = useIntl();
	const [choice, setChoice] = useState<ReplacementChoice | null>(null);

	const savedOptions = viewer.customFieldDefinition?.options ?? [];
	const selectedOption = savedOptions.find(option => option.id === unsavedOption.id) ?? unsavedOption;
	const otherOptions = savedOptions.filter(option => option.id !== selectedOption.id);
	const {displayName} = selectedOption;

	// How many records use the option, when the service counted them. Only a service that applies the replacement
	// sends a count; an older one only says whether any record uses it, and ignores a replacement. So the dialog
	// asks for a choice only with a count, and otherwise says the option is removed from those records, which is
	// what that service does.
	const usedCount = selectedOption.usedCount ?? null;
	const inUse = usedCount !== null ? usedCount > 0 : selectedOption.isUsed;
	const asksForChoice = usedCount !== null && usedCount > 0;

	let message: string;
	if (!inUse) {
		message = formatMessage({id: 'settings.custom-fields.dropdown-options.delete_modal.delete_confirmation_message'});
	} else if (usedCount === null) {
		message = formatMessage({id: 'settings.custom-fields.dropdown-options.delete_modal.in_use_unknown'}, {entityType});
	} else {
		message = formatMessage(
			{id: 'settings.custom-fields.dropdown-options.delete_modal.in_use_count'},
			{count: usedCount, entityType, displayName}
		);
	}
	const removeLabel = formatMessage(
		{id: 'settings.custom-fields.dropdown-options.delete_modal.remove_from_records'},
		{count: usedCount ?? 0, entityType}
	);

	const handleDelete = () => {
		onConfirm(choice && choice !== REMOVE ? choice.id : null);
		closeModal();
	};

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>
				<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.delete_dropdown_option" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<Text>{message}</Text>
					<Label>
						<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.display_name" />
					</Label>
					<Text>{displayName}</Text>
					{asksForChoice && (
						<>
							<StyledDiv>
								<Separator />
							</StyledDiv>
							<FlexColumn gap={'xs'}>
								<Label size={'2'}>
									<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.replace_with" />
								</Label>
								<DropdownList>
									<DropdownList.Trigger>
										<ButtonDropdownField isDefaultLabel={!choice}>
											{choice ? (
												<Text>{choice === REMOVE ? removeLabel : choice.displayName}</Text>
											) : (
												<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.dropdown_default" />
											)}
										</ButtonDropdownField>
									</DropdownList.Trigger>
									<DropdownList.Content offset={10} useTriggerWidth>
										{otherOptions.map(option => (
											<DropdownList.Item key={option.id} onClick={() => setChoice(option)}>
												{option.displayName}
											</DropdownList.Item>
										))}
										<DropdownList.Item key={REMOVE} onClick={() => setChoice(REMOVE)}>
											{removeLabel}
										</DropdownList.Item>
									</DropdownList.Content>
								</DropdownList>
							</FlexColumn>
						</>
					)}
					<FlexRow>
						<Modal.PrimaryFooterButton onPress={handleDelete} disabled={asksForChoice && !choice} dataCy="delete">
							<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.delete_dropdown_option" />
						</Modal.PrimaryFooterButton>
						<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
							<FormattedMessage id="common.cancel" />
						</Modal.SecondaryFooterButton>
					</FlexRow>
				</FlexColumn>
			</Modal.Content>
		</Modal>
	);
};

export const DeleteCustomFieldDropdownOptionConfirmationModalQuery = graphql`
	query DeleteCustomFieldDropdownOptionConfirmationModalQuery($customFieldId: ID, $hasCustomFieldId: Boolean!) {
		viewer {
			actualPersonId
			component(name: "custom_field_dropdown_options_modal")
			...DeleteCustomFieldDropdownOptionConfirmationModal_viewer
				@arguments(customFieldId: $customFieldId)
				@include(if: $hasCustomFieldId)
		}
	}
`;

export const DeleteCustomFieldDropdownOptionConfirmationModal = createFragmentContainer(
	DeleteCustomFieldDropdownOptionConfirmationModalInternal,
	{
		viewer: graphql`
			fragment DeleteCustomFieldDropdownOptionConfirmationModal_viewer on Viewer
			@argumentDefinitions(customFieldId: {type: "ID"}) {
				customFieldDefinition(id: $customFieldId) {
					... on DropdownCustomField {
						options {
							id
							displayName
							isUsed
							usedCount
						}
					}
					... on MultiSelectCustomField {
						options {
							id
							displayName
							isUsed
							usedCount
						}
					}
				}
			}
		`,
	}
);
